<template>
    <div class="w-full absolute top-0">
        <div class="relative flex flex-col gap-24 items-center justify-center pt-34 mb-28">
            <div class="flex flex-col container !gap-16 md:!flex md:!flex-row">
                <div class="view-h1 ">{{data.title[mainStore.active_locale]}}</div>
                <div class="">
                    <RenderedHTML :value="data.subtitle" />
                </div>
            </div>
            <div class="w-full max-w-hd">
                <ResponsiveImage v-if="data?.['hero-image']" :image-id="data['hero-image']" class="object-cover w-full h-full ease-in-out"/>
            </div>

            <div class="flex flex-col gap-4 container">
                <div class="view-h2 w-full md:col-span-4">{{data.our_services.title[mainStore.active_locale]}}</div>
                <div class="flex flex-col gap-16 w-full md:col-start-6 md:col-span-6">
                    <div class="">
                        <RenderedHTML :value="data.our_services.subtitle" />
                    </div>
                    <Services :services="data.our_services['our-services-items']" />
                </div>
            </div>

            <div class="flex flex-col gap-16 md:!gap-y-16 container">
                <div class="view-h2 md:col-span-full">{{ data['what-we-make'].title[mainStore.active_locale] }}</div>
                <div class="flex flex-col column-gap-4 gap-y-16 md:gap-y-8 col-span-full">
                    <AboutImage v-for="(about_image, i) in data?.['what-we-make']['what-we-make-items']" :title="about_image.title[mainStore.active_locale]" :body="about_image.body" :image="about_image.image" :is-even="(i +1) % 2 === 0"></AboutImage>
                </div>
            </div>

            <BlockText large-title :content="{title: data?.['lab']?.['title'], body: data?.['lab']?.['body']}" />

            <ImageWithTextOverlay :content="{image: data?.lab?.image}"/>


            <div class="flex flex-col gap-4 px-0 items-center overflow-hidden w-screen">
                <BlockText large-title :content="{title: data.people.title, body: data.people.subtitle}" />
                <div class="w-full !max-w-hd col-span-full overflow-hidden">
                    <ImageCarousel :people="people" autoplay/>
                </div>
            </div>

            <div class="container grid grid-cols-12">
                <div class="view-h2 col-span-full md:col-span-5 lg:col-start-2 lg:col-end-6">{{ data.jobs.title[mainStore.active_locale] }}</div>
                <div class="col-span-full md:col-start-6 md:col-end-12 lg:col-end-11">
                    <div class="">
                        <RenderedHTML :value="data.jobs.body" />
                    </div>
                </div>
            </div>

        </div>
        <Footer />
    </div>
</template>

<script lang="ts" setup>
import Header from "~/components/view/Header.vue"
import { Pages } from "#shared/types/Pages"
import Services from "~/components/view/Services.vue"
import AboutImage from "~/components/view/AboutImage.vue"
import ImageCarousel from "~/components/view/ImageCarousel.vue"
import RenderedHTML from "~/components/view/RenderedHTML.vue"
import BlockText from "~/components/view/BlockText.vue"
import ImageWithTextOverlay from "~/components/view/ImageWithTextOverlay.vue"
import Footer from "~/components/view/Footer.vue"
import SetImg from "~/components/view/SetImg.vue"

const mainStore = useMainStore()
const siteStore = useSiteStore()
const { t, locale } = useI18n({useScope: 'local'})
const localePath = useLocalePath()
const config = useRuntimeConfig()

const siteUrl = String(config.public.publicHost || 'https://yipp.nl').replace(/\/$/, '')
const canonicalUrl = computed(() => `${siteUrl}${localePath('/about', locale.value)}`)

useHead(() => ({
    title: t('seo.title')
}))
useSeoMeta({
    description: () => t('seo.description'),
    ogTitle: () => t('seo.title'),
    ogDescription: () => t('seo.description'),
    ogUrl: () => canonicalUrl.value
})

const { data: {value: { data: {data: data}     }} } = await useAsyncData(
    'website-about',
    () => $fetch<any>('/api/bucketContent/website-about-us/latest')
)

const { content: people } = await useContentReferences({
    sourceBucket: 'website-about-us',
    sourceData: data,
    fieldPath: 'people.people'
})

definePageMeta({
    layout: 'view',
    pageTransition: {
        onBeforeEnter: (el) => {
            if (window) window.scroll({top: 0})
        }
    }
})

onMounted(() => {
    siteStore.setIconColor('dark')
    siteStore.setCurrentPage(Pages.ABOUT)
})
</script>

<i18n lang="yaml">
en:
    seo:
        title: "About YIPP — Digital Exhibition & Immersive Studio"
        description: "Meet the studio behind YIPP: our team, our approach, and how we design playful digital experiences for museums, brands, and public spaces."
nl:
    seo:
        title: "Over YIPP — Digital Exhibition & Immersive Studio"
        description: "Maak kennis met YIPP: ons team, onze aanpak en hoe we speelse digitale ervaringen ontwerpen voor musea, merken en publieke ruimtes."
</i18n>
